/**
 * Callouts filled in from the plan: the alignment and stations a pour's marks span, measured along
 * the plan's own stationing (vision/stations.ts) — what an inspector would read off the plan and
 * type, read off it instead.
 *
 * A callout is filled in only when every mark it points at lies across a stationed centreline the
 * plan measures there; otherwise it is left for the inspector (a callout is never filled in with
 * anything the plan does not say). Stations are whole feet, as they are written in the field. A
 * mark across two centreline at once (a duct bank carrying SC1 and SC2) names both, "SC1/SC2", when
 * it spans the same stations on each, and else each on a line of its own. A filled-in callout
 * (`auto`) follows its marks as they are reshaped, until its stations are typed over.
 */
import type { Callout, CalloutRange, Highlight, MapDoc } from '../../shared/doc.ts';
import { stationText } from '../model/station.ts';
import { stationsOfOutline, type PlanStations } from '../vision/stations.ts';
import { shapeToPolygon } from './outline.ts';

/** The stretches of stationed line one mark spans (null: none the plan measures). */
export function stretchesOfMark(mark: Highlight, st: PlanStations): CalloutRange[] | null {
  const found = stationsOfOutline(st, shapeToPolygon(mark.shape));
  if (!found.length) return null;
  const rows = found.map((f) => ({ name: f.alignment.name ?? '', from: Math.round(f.from), to: Math.round(f.to) }));
  // one pour across two alignments over the same stations is one statement: "SC1/SC2 stations …"
  const merged: { names: string[]; from: number; to: number }[] = [];
  for (const r of rows) {
    const same = merged.find((m) => m.from === r.from && m.to === r.to);
    if (same && r.name && same.names.every((n) => n)) same.names.push(r.name);
    else merged.push({ names: [r.name], from: r.from, to: r.to });
  }
  return merged.map((m) => ({ alignment: [...new Set(m.names)].sort().join('/'), from: stationText(m.from), to: stationText(m.to) }));
}

/**
 * The stretches a callout's marks span, its own mark's first (null: a mark of it is not on a
 * stationed line, or it points at none).
 */
export function stretchesOf(c: Callout, doc: MapDoc, st: PlanStations): CalloutRange[] | null {
  if (doc.kind !== 'concrete' || c.template === 'custom') return null;
  const ids = [c.targetId, ...(c.also ?? []).map((a) => a.targetId)].filter((x): x is string => !!x);
  if (!ids.length) return null;
  const out: CalloutRange[] = [];
  for (const id of new Set(ids)) {
    const mark = doc.items.find((x): x is Highlight => x.t === 'hl' && x.id === id);
    if (!mark) return null;
    const s = stretchesOfMark(mark, st);
    if (!s) return null;
    for (const r of s) if (!out.some((o) => o.alignment === r.alignment && o.from === r.from && o.to === r.to)) out.push(r);
  }
  return out;
}

/** The callout with its alignment and stations filled in from the plan, or as it was when the plan does not say. */
export function withAutoStations(c: Callout, doc: MapDoc, st: PlanStations | null): Callout {
  if (!st) return c;
  const s = stretchesOf(c, doc, st);
  if (!s || !s.length) return c;
  return fill(c, s);
}

function fill(c: Callout, s: CalloutRange[]): Callout {
  const [first, ...rest] = s;
  const { ranges: _r, ...base } = c;
  const next: Callout = { ...base, template: 'between', alignment: first!.alignment, from: first!.from, to: first!.to, auto: true };
  if (rest.length) next.ranges = rest;
  return next;
}

const same = (a: Callout, b: Callout) => a.alignment === b.alignment && a.from === b.from && a.to === b.to && JSON.stringify(a.ranges ?? []) === JSON.stringify(b.ranges ?? []);

/**
 * Every filled-in callout brought up to date with its marks (a mark reshaped, added or let go):
 * the doc as it was when nothing changed. A callout whose marks the plan no longer measures has its
 * stations cleared (never left saying what they no longer are); it fills in again when they are.
 */
export function refreshAutoStations(doc: MapDoc, st: PlanStations | null): MapDoc {
  if (!st || doc.kind !== 'concrete' || !doc.items.some((x) => x.t === 'callout' && x.auto)) return doc;
  let changed = false;
  const items = doc.items.map((x) => {
    if (x.t !== 'callout' || !x.auto) return x;
    const s = stretchesOf(x, doc, st);
    let next: Callout;
    if (s && s.length) next = fill(x, s);
    else {
      const { ranges: _r, ...base } = x;
      next = { ...base, from: '', to: '' };
    }
    if (same(next, x)) return x;
    changed = true;
    return next;
  });
  return changed ? { ...doc, items } : doc;
}
